import React from "react";
import {
  AriaRadioGroupProps,
  AriaRadioProps,
  useRadio,
  useRadioGroup,
  VisuallyHidden,
} from "react-aria";
import { RadioGroupState, useRadioGroupState } from "react-stately";
import { Flex, FlexProps } from "../flex";
import { InputError } from "../input-error";
import { Text } from "../text";
import { InputLabel } from "../input-label";
import { cx } from "../../utils/dom";
import { Color, InputAppearance } from "../../utils/theme";
import { StyleProps } from "../../utils/types";

export interface RadioProps extends AriaRadioProps {
  className?: string;
  style?: React.CSSProperties;
  size?: "xs" | "sm" | "md" | "lg";
  appearance?: InputAppearance;
  color?: Color;
}

export const RadioContext = React.createContext<RadioGroupState | null>(null);

export const Radio = React.forwardRef<HTMLLabelElement, RadioProps>(
  (props, ref) => {
    const state = React.useContext(RadioContext);

    const {
      size = "sm",
      className,
      style,
      appearance,
      color,
      ...otherProps
    } = props;
    if (!state) throw new Error("Radio must be used within a RadioGroup");

    const inputRef = React.useRef(null);
    const { inputProps } = useRadio(otherProps, state, inputRef);

    return (
      <label style={style} className={cx([className, "p-radio"])} ref={ref}>
        <VisuallyHidden>
          <input {...inputProps} ref={inputRef}></input>
        </VisuallyHidden>
        <div
          data-color={color}
          className="p-fake-radio"
          data-appearance={appearance}
          data-checked={inputProps.checked}
          data-size={size}
        />
        {props.children}
      </label>
    );
  }
);

interface RadioGroupProps
  extends AriaRadioGroupProps,
    FlexProps,
    StyleProps<"wrapper"> {
  children?: React.ReactNode;
}

export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      stack = true,
      direction,
      gap = "xs",
      rowGap,
      align,
      justify,
      wrap,
      columnGap,
      className,
      classNames,
      style,
      styles,
      ...props
    },
    ref
  ) => {
    const { children, label, description, errorMessage } = props;
    const state = useRadioGroupState(props);
    const { radioGroupProps, labelProps, descriptionProps, errorMessageProps } =
      useRadioGroup(props, state);

    return (
      <Flex
        style={{ ...style, ...styles?.wrapper }}
        className={cx(className, classNames?.wrapper, "p-radio-group")}
        ref={ref}
        stack={stack}
        gap={gap}
        direction={direction}
        rowGap={rowGap}
        columnGap={columnGap}
        align={align}
        justify={justify}
        wrap={wrap}
        {...radioGroupProps}
      >
        {label && <InputLabel {...labelProps}>{label}</InputLabel>}
        {description && (
          <Text variant="light" {...descriptionProps}>
            {description}
          </Text>
        )}

        <RadioContext.Provider value={state}>{children}</RadioContext.Provider>

        {errorMessage && (
          <InputError {...errorMessageProps}>{errorMessage}</InputError>
        )}
      </Flex>
    );
  }
);

RadioGroup.displayName = "RadioGroup";
Radio.displayName = "Radio";
